<template>
  <div
    class="field-checkbox"
    :class="{ 'has-error': !!errorMessage, success: meta.valid && touched }"
  >
    <label v-if="label && position === 'before'" :for="computedId">
      <Icon v-if="icon" type="favicon" :icon="icon" class="Icon" />
      {{ label }}
    </label>
    <!-- {{  isArrayMode }} -- {{ data }} -->
    <!-- NEVER use v-model on native checkbox with custom values -->
    <input
      type="checkbox"
      :id="computedId"
      :name="name"
      :checked="isChecked"
      @change="onChange"
      @blur="handleBlur"
      class="w-fit"
      :class="{ 'p-invalid': !!errorMessage, success: meta.valid && touched }"
      v-bind="attrs"
    />

    <label v-if="label && position === 'after'" :for="computedId">
      <Icon v-if="icon" type="favicon" :icon="icon" class="Icon" />
      {{ label }}
    </label>

    <small class="p-error">{{ errorMessage }}</small>
  </div>
</template>

<script setup>
import { useField } from "vee-validate";
import { computed, watch, useAttrs } from "vue";

const props = defineProps({
  modelValue: { type: [Array, Boolean, String, Number, Object], default: null },
  value: { type: [String, Number, Boolean, Object] }, // undefined → single mode
  name: { type: String, required: true },
  label: String,
  position: { type: String, default: "before" },
  icon: String,
  trueValue: { type: [String, Number, Boolean], default: 1 },
  falseValue: { type: [String, Number, Boolean], default: 0 },
  rules: [String, Object, Function],
});

const emit = defineEmits(["update:modelValue"]);
const attrs = useAttrs();

// === VeeValidate ===
const {
  value: fieldValue,
  errorMessage,
  handleBlur,
  meta,
  touched,
  handleChange,
} = useField(props.name, props.rules, {
  initialValue: props.modelValue ?? (props.value !== undefined ? [] : props.falseValue),
});

// === Mode Detection ===
const isArrayMode = computed(() => props.value !== undefined && Array.isArray(fieldValue.value));

// === Is this checkbox checked? ===
const isChecked = computed(() => {
  if (isArrayMode.value) {
    return Array.isArray(fieldValue.value) && fieldValue.value.includes(props.value);
  }
  return fieldValue.value === props.trueValue;
});

// === Handle change ===
function onChange(e) {
  const checked = e.target.checked;
  let newValue;

  if (isArrayMode.value) {
    const arr = Array.isArray(fieldValue.value) ? [...fieldValue.value] : [];
    if (checked) {
      if (!arr.includes(props.value)) {
        arr.push(props.value);
      }
    } else {
      const index = arr.indexOf(props.value);
      if (index > -1) arr.splice(index, 1);
    }
    newValue = arr;
  } else {
    newValue = checked ? props.trueValue : props.falseValue;
  }

  fieldValue.value = newValue;
  handleChange(newValue);
  emit("update:modelValue", newValue);
}

// === Sync external modelValue changes (e.g. form.resetFields()) ===
watch(
  () => props.modelValue,
  (val) => {
    const normalized =
      val == null && props.value === undefined ? props.falseValue : val;
    if (normalized !== fieldValue.value) {
      fieldValue.value = normalized;
    }
  },
  { immediate: true }
);

const computedId = computed(() => props.name);
</script>